import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { Class } from '@pirouette/shared';
import { Empty } from '@/components/ui/Empty';
import { WeeklyGrid } from '@/components/schedule/WeeklyGrid';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/dancer/')({
  component: DancerSchedule,
});

interface DancerScheduleData {
  dancerName: string | null;
  classes: Class[];
}

function uniqueClasses(rows: { classes: Class | null }[]): Class[] {
  const seen = new Set<string>();
  return rows
    .map((r) => r.classes)
    .filter((c): c is Class => c !== null && c.deleted_at === null)
    .filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
}

function useDancerSchedule() {
  return useQuery({
    queryKey: ['dancer-schedule'],
    queryFn: async (): Promise<DancerScheduleData> => {
      const userResponse = await supabase.auth.getUser();
      const userId = userResponse.data.user?.id;
      if (!userId) return { dancerName: null, classes: [] };

      const { data: dancer, error: dancerError } = await supabase
        .from('dancers')
        .select('id, first_name, preferred_name')
        .eq('auth_user_id', userId)
        .maybeSingle();
      if (dancerError) throw dancerError;
      if (!dancer) return { dancerName: null, classes: [] };

      const { data, error } = await supabase
        .from('enrollments')
        .select('classes(*)')
        .eq('dancer_id', dancer.id)
        .eq('status', 'enrolled')
        .is('deleted_at', null);
      if (error) throw error;

      return {
        dancerName: dancer.preferred_name ?? dancer.first_name,
        classes: uniqueClasses((data ?? []) as { classes: Class | null }[]),
      };
    },
  });
}

function DancerSchedule() {
  const schedule = useDancerSchedule();

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Your week
        </p>
        <h1 className="mt-2">Schedule</h1>
        {schedule.data?.dancerName && (
          <p className="mt-2 text-[var(--color-ink-muted)]">
            Classes for {schedule.data.dancerName}.
          </p>
        )}
      </header>

      {schedule.isLoading && <p aria-live="polite">Loading…</p>}

      {schedule.data && !schedule.data.dancerName && (
        <Empty
          title="No dancer profile linked"
          description="Ask the studio admin to connect this login to a dancer profile."
        />
      )}

      {schedule.data?.dancerName && schedule.data.classes.length === 0 && (
        <Empty
          title="No classes yet"
          description="Once you are enrolled, your weekly schedule will appear here."
        />
      )}

      {schedule.data && schedule.data.classes.length > 0 && (
        <WeeklyGrid classes={schedule.data.classes} />
      )}
    </div>
  );
}
